import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    INotesOfAnnualReportDTO,
    INotesTemplateDTO,
    INotesTemplateEntityDTO,
} from '@modeso/types__aumico-lib-notes-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import * as fromActions from '../actions/notesTemplates.actions';
import { INotesTemplateByLegalFormAndAccountBasisRequest } from '../interfaces/requests/INotesTemplateByLegalFormAndAccountBasisRequest.interface';
import { AumicoNotesService } from '../services/notes.service';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesTemplatesEffects');

@Injectable()
export class NotesTemplatesEffects {
    constructor(private actions$: Actions, private service: AumicoNotesService) {}

    getNotesTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getNotesTemplates.type),
            mergeMap(() => {
                return this.service.getNotesTemplates().pipe(
                    map((response: INotesTemplateDTO[]) => {
                        debug(response);
                        return fromActions.onGetNotesTemplatesSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onGetNotesTemplatesFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getNotesTemplateByLegalFormAndAccountBasis$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getNotes.type),
            mergeMap((action: fromActions.ActionWithPayload<INotesTemplateByLegalFormAndAccountBasisRequest>) => {
                return this.service.getNotesTemplateByLegalFormAndAccountBasis(action).pipe(
                    map((response: INotesOfAnnualReportDTO) => {
                        debug(response);
                        return fromActions.onGetNotesSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onGetNotesFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getNotesTemplateById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getNotesTemplateById.do.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getNotesTemplateById(action.payload).pipe(
                    map((response: INotesTemplateDTO) => {
                        debug(response);
                        return fromActions.getNotesTemplateById.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getNotesTemplateById.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    createNotesTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.createNotesTemplate.type),
            mergeMap((action: fromActions.ActionWithPayload<INotesTemplateEntityDTO>) => {
                return this.service.createNotesTemplate(action).pipe(
                    map((response: INotesTemplateDTO) => {
                        debug(response);
                        return fromActions.onCreateNotesTemplateSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onCreateNotesTemplateFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateNotesTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateNotesTemplate.type),
            mergeMap((action: fromActions.ActionWithPayload<INotesTemplateEntityDTO>) => {
                return this.service.updateNotesTemplate(action).pipe(
                    map((response: INotesTemplateDTO) => {
                        debug(response);
                        return fromActions.onUpdateNotesTemplateSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onUpdateNotesTemplateFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    errorOnNotesTypes$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    fromActions.onCreateNotesTemplateFailed.type,
                    fromActions.onGetNotesTemplatesFailed.type,
                    fromActions.onUpdateNotesTemplateFailed.type,
                ),
                tap((action: fromActions.ActionWithPayload<HttpErrorResponse>) =>
                    this.handleOnLoadAllErrors(action.payload),
                ),
            ),
        { dispatch: false },
    );

    handleOnLoadAllErrors(error) {
        debug('handleOnLoadAllErrors');
        debug(error);
        return error;
    }
}
